import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ACTIONS, CallBackProps, STATUS } from 'react-joyride';
import { useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { RootState } from 'app/store';

import { closeSidebar } from 'features/sidebar/sidebarSlice';
import { selectTourState } from 'features/tour/tourSelectors';
import { startTour as startTourAction } from 'features/tour/tourSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import { useAppDispatch, useAppSelector } from 'hooks/useTypedRedux';

import { scrollToElement } from 'utils/scrollToElement';
import { scrollToTop } from 'utils/scrollToTop';
import { adjustElementPosition } from 'utils/tour/adjustElementPosition';
import { dismissGuide } from 'utils/tour/dismissGuide';
import { filterTourSteps } from 'utils/tour/filterTourSteps';
import { TourEngine } from 'utils/tour/tourEngine';
import { TourNavigator } from 'utils/tour/tourNavigator';
import { tourRefRegistry } from 'utils/tour/tourRefRegistry';
import { waitForElement } from 'utils/tour/waitForElement';
import { waitForElementPositionStable } from 'utils/tour/waitForElementPositionStable';
import { waitForTargetMount } from 'utils/tour/waitForTargetMount';

import { GuideCompleted, TourStep } from 'types/tour';

import { HOME } from 'constants/path';
import {
  TOUR_CLOSE_SIDEBAR_DELAY_MS,
  TOUR_ELEMENT_POSITION_STABLE_DURATION_MS,
  TOUR_SCROLL_TO_ELEMENT_OFFSET,
  TOUR_SCROLL_TO_TOP_DELAY_MS,
  TOUR_START_FULL_TOUR_DELAY_MS,
  TOUR_WAIT_FOR_TARGET_MOUNT_TIMEOUT_MS
} from 'constants/tour';

export const useTourRef = (
  selector: string,
  element: RefObject<HTMLElement> | HTMLElement | null
): void => {
  useEffect(() => {
    if (!selector) {
      return;
    }

    if (element) {
      tourRefRegistry.register(selector, element);
    }

    return () => {
      tourRefRegistry.unregister(selector);
    };
  }, [selector, element]);
};

export const useTourRefCallback = (
  selector: string
): RefObject<HTMLElement> => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!selector) {
      return;
    }

    if (ref.current) {
      tourRefRegistry.register(selector, ref);
    }

    return () => {
      tourRefRegistry.unregister(selector);
    };
  }, [selector]);

  return ref;
};

export const useScrollToTarget = () => {
  return useCallback(async (selector: string): Promise<boolean> => {
    if (!selector || typeof selector !== 'string') {
      return false;
    }

    await new Promise((resolve) => setTimeout(resolve, 300));

    try {
      const element = await waitForElement(selector);

      if (!element) {
        return false;
      }

      const htmlElement = element as HTMLElement;

      if (!document.contains(htmlElement)) {
        return false;
      }

      try {
        await waitForElementPositionStable(
          selector,
          TOUR_ELEMENT_POSITION_STABLE_DURATION_MS
        );
      } catch (error) {
        console.warn(error instanceof Error ? error.message : error);
        return false;
      }

      scrollToElement(htmlElement, TOUR_SCROLL_TO_ELEMENT_OFFSET);

      await new Promise((resolve) => setTimeout(resolve, 300));

      adjustElementPosition(selector);

      return true;
    } catch (error) {
      return false;
    }
  }, []);
};

export const useStartTour = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const tourState = useAppSelector(selectTourState);

  return useCallback(
    (steps: TourStep[], startPage?: string, isFullGuide: boolean = false) => {
      const filteredSteps = filterTourSteps({
        steps,
        applicationId: tourState.applicationId,
        jobId: tourState.jobId
      });

      if (isFullGuide && location.pathname !== HOME) {
        dispatch(closeSidebar());

        setTimeout(() => {
          navigate(HOME);
        }, TOUR_CLOSE_SIDEBAR_DELAY_MS);

        setTimeout(() => {
          scrollToTop();
        }, TOUR_SCROLL_TO_TOP_DELAY_MS);

        setTimeout(() => {
          dispatch(
            startTourAction({
              steps: filteredSteps,
              startPage,
              isFullGuide
            })
          );
        }, TOUR_START_FULL_TOUR_DELAY_MS);

        return;
      }

      dispatch(
        startTourAction({ steps: filteredSteps, startPage, isFullGuide })
      );
    },
    [
      dispatch,
      navigate,
      location.pathname,
      tourState.applicationId,
      tourState.jobId
    ]
  );
};

export const useHandleJoyrideCallback = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const catchError = useErrorCatch();
  const scrollToTarget = useScrollToTarget();
  const navigate = useNavigate();
  const store = useStore<RootState>();

  const getTourState = useCallback(
    () => selectTourState(store.getState()),
    [store]
  );

  const getRootState = useCallback(() => store.getState(), [store]);

  const closeSidebarHandler = useCallback(() => {
    dispatch(closeSidebar());
  }, [dispatch]);

  const errorHandler = useCallback(
    (error: unknown) => {
      catchError({
        error,
        default: {
          message: t('error.message.finishTourFailed')
        }
      });
    },
    [catchError, t]
  );

  const onDismissGuide = useCallback(
    (guideCompleted: GuideCompleted) =>
      dismissGuide({
        guideCompleted,
        dispatch,
        catchError,
        errorMessage: t('error.message.finishTourFailed'),
        onClose: undefined
      }),
    [catchError, dispatch, t]
  );

  const navigator = useMemo(
    () =>
      new TourNavigator({
        navigate,
        scrollToTarget,
        dispatch,
        getState: getTourState
      }),
    [dispatch, getTourState, navigate, scrollToTarget]
  );

  const engine = useMemo(
    () =>
      new TourEngine({
        getState: getTourState,
        getRootState,
        dispatch,
        navigator,
        onCloseSidebar: closeSidebarHandler,
        onError: errorHandler,
        onDismissGuide
      }),
    [
      closeSidebarHandler,
      dispatch,
      errorHandler,
      getRootState,
      getTourState,
      navigator,
      onDismissGuide
    ]
  );

  return useCallback(
    async ({ action, type, index = 0, step, status }: CallBackProps) => {
      try {
        if (type === 'error:target_not_found') {
          const state = getTourState();
          const currentStep = state.steps[index];
          const targetSelector = currentStep?.target as string | undefined;

          // Wait for target element to mount
          if (targetSelector) {
            const elementFound = await waitForTargetMount(
              targetSelector,
              TOUR_WAIT_FOR_TARGET_MOUNT_TIMEOUT_MS
            );

            if (elementFound) {
              await engine.beforeStep(index, step);
              return;
            }
          }
          // Target still not mounted - skip to next step or stop
          if (index < state.steps.length - 1) {
            await engine.next();
          } else {
            engine.stop();
          }
          return;
        }

        if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
          engine.stop();
          return;
        }

        switch (type) {
          case 'step:before':
            await engine.beforeStep(index, step);
            return;

          case 'step:after':
            switch (action) {
              case ACTIONS.NEXT:
                await engine.next();
                return;

              case ACTIONS.PREV:
                await engine.prev();
                return;
            }
            break;
        }

        if (action === ACTIONS.CLOSE) {
          engine.stop();
        }
      } catch (error) {
        engine.stop();
      }
    },
    [engine]
  );
};
